import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

/** Las letras del RIF, en el orden en que más se usan en un alta de comercio. */
const LETRAS = [
  { codigo: 'J', nombre: 'J · Jurídica' },
  { codigo: 'G', nombre: 'G · Gobierno' },
  { codigo: 'V', nombre: 'V · Venezolano' },
  { codigo: 'E', nombre: 'E · Extranjero' },
  { codigo: 'P', nombre: 'P · Pasaporte' },
  { codigo: 'C', nombre: 'C · Comunal' },
];

/**
 * El RIF en dos partes: la letra (J por omisión) y el número (minuta del 5-oct, «formato del RIF»;
 * Juan, 2026-10-08: «deberías poder colocar por defecto J en el RIF, para que sea más fácil el
 * formato»). Se escribe sólo el número; el guion antes del dígito verificador lo pone solo, y
 * `valor` sale siempre como «J-12345678-9». Se usa con `[(valor)]`.
 */
@Component({
  selector: 'app-rif-input',
  template: `
    <div class="rif-fila">
      <div class="kb-select-envoltorio rif-letra">
        <select class="kb-select" [attr.aria-label]="'Letra del RIF'" [ngModel]="letra"
                (ngModelChange)="letra = $event; emitir()" [disabled]="deshabilitado">
          <option *ngFor="let l of LETRAS" [value]="l.codigo">{{ l.nombre }}</option>
        </select>
        <i class="fas fa-chevron-down kb-select-flecha" aria-hidden="true"></i>
      </div>
      <input [id]="inputId" type="text" inputmode="numeric" class="kb-input font-mono rif-numero"
             placeholder="12345678-9" maxlength="11" [ngModel]="numero" (ngModelChange)="onNumero($event)"
             [ngClass]="{'kb-input-invalido': invalido}" [attr.aria-invalid]="invalido"
             [disabled]="deshabilitado" (blur)="tocado.emit()">
    </div>`,
  styles: [`
    .rif-fila { display: flex; gap: .5rem; }
    .rif-letra { flex: 0 0 9.5rem; }
    .rif-numero { flex: 1 1 auto; min-width: 0; }
  `],
})
export class RifInputComponent implements OnChanges {
  @Input() valor: string | null | undefined = '';
  @Input() inputId = '';
  @Input() invalido = false;
  @Input() deshabilitado = false;
  @Output() valorChange = new EventEmitter<string>();
  @Output() tocado = new EventEmitter<void>();

  LETRAS = LETRAS;
  letra = 'J';
  numero = '';

  /** Lo que llega de afuera («j401234567», «J-40123456-7» o vacío) se parte en letra y número. Si
   *  es lo mismo que ya se está mostrando, no se toca: así no salta el cursor mientras se escribe. */
  ngOnChanges(): void {
    const limpio = String(this.valor || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (limpio === this.limpioActual) return;
    const m = /^([A-Z])?(\d*)/.exec(limpio);
    if (m && m[1] && LETRAS.some((l) => l.codigo === m[1])) this.letra = m[1];
    this.numero = this.formatear((m && m[2]) || '');
  }

  private get limpioActual(): string { return this.numero ? `${this.letra}${this.numero.replace(/\D/g, '')}` : ''; }

  /** 9 dígitos → «12345678-9»; menos, tal cual. */
  private formatear(d: string): string { return d.length === 9 ? `${d.slice(0, 8)}-${d.slice(8)}` : d; }

  onNumero(escrito: string): void {
    this.numero = this.formatear(String(escrito || '').replace(/\D/g, '').slice(0, 9));
    this.emitir();
  }

  emitir(): void { this.valorChange.emit(this.numero ? `${this.letra}-${this.numero}` : ''); }
}
